<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useLocale } from 'shared/composables/useLocale';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import Button from 'next/button/Button.vue';
import Dialog from 'next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();
const { resolvedLocale } = useLocale();

const PREFIX = 'INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC';
// Etapas que a Meta não aceita de novo: em andamento, aceitas ou sem resposta.
const LOCKED = ['requesting', 'requested', 'unknown'];

const state = ref(null);
const loadFailed = ref(false);
const isRequesting = ref(false);
const confirmDialog = ref(null);

const TONES = {
  success: 'bg-n-teal-3 text-n-teal-11',
  progress: 'bg-n-blue-3 text-n-blue-11',
  warning: 'bg-n-amber-3 text-n-amber-11',
  danger: 'bg-n-ruby-3 text-n-ruby-11',
  neutral: 'bg-n-alpha-2 text-n-slate-11',
};

const stepBadge = (entry = {}) => {
  const map = {
    requesting: ['REQUESTING', 'progress'],
    requested: ['REQUESTED', 'success'],
    failed: ['FAILED', 'danger'],
    unknown: ['UNKNOWN', 'warning'],
  };
  const [key, tone] = map[entry.status] || ['NONE', 'neutral'];
  return { label: t(`${PREFIX}.STATUS.${key}`), tone };
};

const historyEntry = computed(() => state.value?.history || {});
const progress = computed(() => Number(historyEntry.value.progress || 0));
const isDeclined = computed(() => !!historyEntry.value.declined_at);

const historyBadge = computed(() => {
  if (isDeclined.value) {
    return {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.STATUS.DECLINED'),
      tone: 'danger',
    };
  }
  if (progress.value >= 100) {
    return {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.STATUS.COMPLETED'),
      tone: 'success',
    };
  }
  if (progress.value > 0) {
    return {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.STATUS.RECEIVING', {
        progress: progress.value,
      }),
      tone: 'progress',
    };
  }
  return stepBadge(historyEntry.value);
});

const rows = computed(() => [
  {
    key: 'contacts',
    icon: 'i-hugeicons-user-group',
    label: t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.CONTACTS'),
    badge: stepBadge(state.value?.contacts),
  },
  {
    key: 'history',
    icon: 'i-hugeicons-message-multiple-01',
    label: t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.HISTORY'),
    badge: historyBadge.value,
  },
]);

const windowOpen = computed(() => state.value?.window_open === true);

const canRequest = computed(
  () =>
    isAdmin.value &&
    windowOpen.value &&
    [state.value?.contacts, state.value?.history].some(
      entry => !LOCKED.includes(entry?.status)
    )
);

const showUnknownHelp = computed(
  () =>
    !isDeclined.value &&
    progress.value === 0 &&
    [state.value?.contacts, state.value?.history].some(
      entry => entry?.status === 'unknown'
    )
);

const deadlineLabel = computed(() => {
  if (!state.value?.deadline) return '';
  const date = new Intl.DateTimeFormat(resolvedLocale.value, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(state.value.deadline));
  return t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.DEADLINE', { date });
});

const load = async () => {
  try {
    const { data } = await InboxHealthAPI.getCoexistenceSync(props.inboxId);
    state.value = data;
    loadFailed.value = false;
  } catch {
    loadFailed.value = true;
  }
};

const requestSync = async () => {
  if (isRequesting.value) return;
  isRequesting.value = true;
  try {
    const { data } = await InboxHealthAPI.requestCoexistenceSync(props.inboxId);
    state.value = data;
    confirmDialog.value?.close();
    useAlert(t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.SUCCESS'));
  } catch (error) {
    confirmDialog.value?.close();
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.ERROR')
    );
    await load();
  } finally {
    isRequesting.value = false;
  }
};

onMounted(() => {
  if (isAdmin.value) load();
});
</script>

<template>
  <section
    v-if="isAdmin && (state || loadFailed)"
    class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak"
  >
    <div class="flex gap-3 items-start">
      <span
        class="flex flex-shrink-0 justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-2 text-n-slate-11"
      >
        <Icon icon="i-hugeicons-smart-phone-01" class="w-4 h-4" />
      </span>
      <div class="min-w-0 space-y-1">
        <p class="mb-0 text-body-main font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.TITLE') }}
        </p>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <p v-if="loadFailed" class="mb-0 text-label-small text-n-ruby-11">
      {{ t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.LOAD_ERROR') }}
    </p>

    <template v-else>
      <dl class="m-0 divide-y divide-n-weak rounded-lg border border-n-weak">
        <div
          v-for="row in rows"
          :key="row.key"
          class="flex flex-wrap gap-2 justify-between items-center px-3 py-2.5"
        >
          <dt class="flex gap-2 items-center text-label text-n-slate-12">
            <Icon :icon="row.icon" class="w-4 h-4 text-n-slate-10" />
            {{ row.label }}
          </dt>
          <dd class="m-0">
            <span
              class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-full"
              :class="TONES[row.badge.tone]"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-current" />
              {{ row.badge.label }}
            </span>
          </dd>
          <div
            v-if="row.key === 'history' && progress > 0 && progress < 100"
            class="w-full h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="h-full rounded-full bg-n-blue-9 transition-all duration-300 motion-reduce:transition-none"
              :style="{ width: `${progress}%` }"
            />
          </div>
        </div>
      </dl>

      <p v-if="isDeclined" class="mb-0 text-label-small text-n-slate-11">
        {{ t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.DECLINED_HELP') }}
      </p>
      <p
        v-else-if="showUnknownHelp"
        class="mb-0 text-label-small text-n-slate-11"
      >
        {{ t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.UNKNOWN_HELP') }}
      </p>
      <p class="mb-0 text-label-small text-n-slate-10">
        {{ t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.MEDIA_NOTE') }}
      </p>

      <div
        class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <span
          class="inline-flex items-center gap-1.5 text-label-small"
          :class="windowOpen ? 'text-n-slate-11' : 'text-n-amber-11'"
        >
          <Icon icon="i-hugeicons-clock-01" class="w-3.5 h-3.5" />
          {{
            windowOpen
              ? deadlineLabel
              : t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.WINDOW_CLOSED')
          }}
        </span>
        <Button
          v-if="canRequest"
          :label="t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.BUTTON')"
          icon="i-hugeicons-download-04"
          size="sm"
          class="flex-shrink-0"
          @click="confirmDialog?.open()"
        />
      </div>
    </template>

    <Dialog
      ref="confirmDialog"
      type="edit"
      :title="t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.CONFIRM_TITLE')"
      :description="
        t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.CONFIRM_DESCRIPTION')
      "
      :confirm-button-label="
        t('INBOX_MGMT.ACCOUNT_HEALTH.COEXISTENCE_SYNC.CONFIRM')
      "
      :is-loading="isRequesting"
      @confirm="requestSync"
    />
  </section>
</template>
